import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toastSuccess, toastError } from '@/components/ui/use-toast';
import { useTranslation } from '@/hooks/useTranslation';
import { API_BASE_URL } from '@/config';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toastError({
        title: t('common.error'),
        description: t('forgotPassword.errors.emailRequired'),
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email: email.trim().toLowerCase() })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t('forgotPassword.errors.sendFailed'));
      }

      setIsSubmitted(true);
      toastSuccess({
        title: t('forgotPassword.success.emailSent'),
        description: data.message,
      });

    } catch (error) {
      console.error('Forgot password failed:', error);
      toastError({
        title: t('forgotPassword.errors.requestFailed'),
        description: error instanceof Error ? error.message : t('forgotPassword.errors.sendFailed'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
        <div className="flex flex-col space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">{t('forgotPassword.success.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('forgotPassword.success.description')}
          </p>
        </div>

        <div className="grid gap-6">
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg dark:bg-green-900/20 dark:border-green-800">
            <h3 className="font-medium text-green-900 dark:text-green-100 mb-2">{t('forgotPassword.success.emailSentTitle')}</h3>
            <p className="text-sm text-green-800 dark:text-green-200 mb-3">
              {t('forgotPassword.success.emailSentMessage', { email })}
            </p>
            <div className="text-sm text-green-800 dark:text-green-200">
              <p className="mb-1">{t('forgotPassword.success.instruction1')}</p>
              <p className="mb-1">{t('forgotPassword.success.instruction2')}</p>
              <p>{t('forgotPassword.success.instruction3')}</p>
            </div>
          </div>
          
          <div className="flex flex-col space-y-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsSubmitted(false);
                setEmail('');
              }}
              className="w-full"
            >
              {t('forgotPassword.success.sendAnother')}
            </Button>

            <Button asChild variant="ghost" className="w-full">
              <Link to="/login">{t('forgotPassword.success.backToLogin')}</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
      <div className="flex flex-col space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">{t('forgotPassword.form.title')}</h1>
        <p className="text-sm text-muted-foreground">
          {t('forgotPassword.form.description')}
        </p>
      </div>

      <div className="grid gap-6">
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="email">{t('forgotPassword.form.emailAddress')}</Label>
              <Input
                id="email"
                placeholder={t('forgotPassword.form.placeholder')}
                type="email"
                autoCapitalize="none"
                autoComplete="email"
                autoCorrect="off"
                disabled={isLoading}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <Button disabled={isLoading} className="w-full">
              {isLoading ? t('forgotPassword.form.sending') : t('forgotPassword.form.sendResetLink')}
            </Button>
          </div>
        </form>
        
        <div className="flex flex-col space-y-2 text-center">
          <Button asChild variant="ghost" className="w-full">
            <Link to="/login">{t('forgotPassword.form.backToLogin')}</Link>
          </Button>

          <div className="text-sm text-muted-foreground space-y-2">
            <p>
              {t('forgotPassword.form.noAccount')}{' '}
              <Link to="/signup" className="underline underline-offset-4 hover:text-primary">
                {t('forgotPassword.form.signUp')}
              </Link>
            </p>
            <p>
              {t('forgotPassword.form.lost2FA')}{' '}
              <Link to="/2fa-reset-request" className="underline underline-offset-4 hover:text-primary">
                {t('forgotPassword.form.request2FAReset')}
              </Link>
            </p>
          </div>
        </div>
      </div>
      
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/20 dark:border-blue-800">
        <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-2">{t('forgotPassword.security.title')}</h3>
        <div className="text-sm text-blue-800 dark:text-blue-200">
          <p className="mb-1">{t('forgotPassword.security.note1')}</p>
          <p className="mb-1">{t('forgotPassword.security.note2')}</p>
          <p>{t('forgotPassword.security.note3')}</p>
        </div>
      </div>
    </div>
  );
}
